import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '../../components/Button/Button';
import { ROUTES } from '../../lib/constants';

import newImage from '../../images/Cityside-Fiber-2-Gigs.jpg';
import FormContainer5050 from '../../components/FormContainer5050/FormContainer5050';
import APIService from '../../lib/api.service';

import styles from './ContactUs.module.scss';

type InquiryType = 'customer' | 'general';

interface OutageComment {
  body: string;
  createdAt: string;
}

interface OutageTicket {
  ticketSubject: string;
  ticketComments: OutageComment[];
}

// TODO: Set to null to hide the banner. Remove when API outage communication is live.
const TEMP_OUTAGE_COMMUNICATION: OutageTicket[] | null = [
  {
    ticketSubject: 'Service Impact',
    ticketComments: [
      {
        body: 'We have completed the re-direction of our network traffic. Speeds and services should improve for our users. Due to this issue impacting a large portion of the internet outside of Cityside Fiber’s networks, there may be further speed and service issues at various internet sites.',
        createdAt: '2026-09-02T22:50:00.000Z',
      },
      {
        body: 'Our engineers have identified an issue with one of our upstream carriers that is impacting speeds to some internet sites and services, we are implementing a repair to bypass the upstream carrier and alleviate the issue.',
        createdAt: '2026-09-02T19:50:00.000Z',
      },
      {
        body: 'Our Network Engineers continue working to fully restore network function, and we estimate services to be restored by 12:30pm.',
        createdAt: '2026-09-02T18:00:00.000Z',
      },
      {
        body: [
          'We are aware of a service issue that may be affecting internet connectivity in your area. Our Network Engineers have identified a problem and are currently working to restore full network functionality.',
          '',
          'We will provide updates as they are available.',
        ].join('\n'),
        createdAt: '2026-09-02T15:30:00.000Z',
      },
    ],
  },
];

const formatOutageDate = (iso: string): string => {
  const d = new Date(iso);
  const pst = new Date(d.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const mm = String(pst.getMonth() + 1).padStart(2, '0');
  const dd = String(pst.getDate()).padStart(2, '0');
  const yy = String(pst.getFullYear()).slice(-2);
  let hours = pst.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const hh = String(hours).padStart(2, '0');
  const min = String(pst.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${yy} ${hh}:${min}${ampm} PST`;
};

const ContactUs = (): React.JSX.Element => {
  const formRef = useRef<HTMLFormElement | null>(null);

  const [inquiryType, setInquiryType] = useState<InquiryType>('customer');
  const [showAnim, setShowAnim] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [showSubmissionSuccess, setShowSubmissionSuccess] = useState(false);
  const [showNetworkError, setShowNetworkError] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [showReasonPrompt, setShowReasonPrompt] = useState(false);
  const [serverMessage, setServerMessage] = useState('');
  const [isDuplicateTicket, setIsDuplicateTicket] = useState(false);
  const [formData, setFormData] = useState<FormData>();

  const [outageData, setOutageData] = useState<OutageTicket[] | null>(TEMP_OUTAGE_COMMUNICATION);
  const [showOutageModal, setShowOutageModal] = useState(false);

  // TODO: Replace TEMP_OUTAGE_COMMUNICATION with API once comm tickets are live.
  // useEffect(() => {
  //   APIService.getRecentOutageCommunication()
  //     .then(res => res.json())
  //     .then(json => {
  //       if (json?.data?.length) setOutageData(json.data);
  //     })
  //     .catch(() => {});
  // }, []);

  const [reasonDropdownOpen, setReasonDropdownOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<{ value: string; label: string } | null>(null);
  const reasonDropdownRef = useRef<HTMLDivElement | null>(null);

  const reasonOptions = [
    { value: 'customer-support', label: 'Customer Support', desc: 'Questions about your account, billing, payments, or general service inquiries.' },
    { value: 'technical-support', label: 'Technical Support', desc: 'Issues with your internet connection, WiFi, speed, or equipment not working as expected.' },
    { value: 'installation-support', label: 'Installation Support', desc: 'Help with scheduling, rescheduling, or questions about your installation or activation.' },
  ];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (reasonDropdownRef.current && !reasonDropdownRef.current.contains(e.target as Node)) {
        setReasonDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onChange = () => {
    if (formRef.current) {
      setFormData(new FormData(formRef.current));
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setShowAnim(true);
    setShowEmailPrompt(false);

    const data: Record<string, string> = {};
    for (const pair of (formData as FormData).entries()) {
      data[`${pair[0]}`] = `${pair[1]}`;
    }
    delete data['contacttype'];

    // Validate email if present
    if (data['email']) {
      const validEmail = new RegExp(/\S+@\S+\.\S+/);
      if (!validEmail.test(data['email'])) {
        setShowEmailPrompt(true);
        setShowAnim(false);
        return;
      }
    }

    let response;

    if (inquiryType === 'customer') {
      if (!selectedReason) {
        setShowReasonPrompt(true);
        setShowAnim(false);
        return;
      }
      data['type'] = selectedReason.label;
    }
    setShowReasonPrompt(false);

    try {
      if (inquiryType === 'customer') {
        response = await APIService.postTicket(JSON.stringify(data));
      } else {
        const generalData: Record<string, string> = {
          firstName: data['firstname'],
          lastName: data['lastname'],
          email: data['email'],
          description: data['description'],
        };
        if (data['phone']) generalData.phone = data['phone'];
        response = await APIService.postTicketInquiry(JSON.stringify(generalData));
      }
      const json = await response.json();
      if (response.ok && json?.success) {
        setServerMessage(json.message ?? '');
        setShowForm(false);
        setShowSubmissionSuccess(true);
      } else {
        setServerMessage(json?.message ?? '');
        setIsDuplicateTicket(response.status === 409);
        setShowForm(false);
        setShowNetworkError(true);
      }
    } catch (error) {
      setShowForm(false);
      setShowNetworkError(true);
    } finally {
      setShowAnim(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{ROUTES.contactUs.title}</title>
        <meta
          name="description"
          content="Contact Cityside Fiber — submit a support ticket or general inquiry."
        />
        <meta property="og:title" content={`${ROUTES.contactUs.title}`} />
        <meta property="og:description" content="Contact Cityside Fiber — submit a support ticket or general inquiry." />
        <meta property="og:url" content="https://www.citysidefiber.com/contact-us" />
      </Helmet>

      {/* Outage Banner */}
      {outageData && outageData.length > 0 && (() => {
        const ticket = outageData[0];
        const comments = [...ticket.ticketComments].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        const latest = comments[0];
        return (
          <>
            <div className={styles.outageBanner}>
              <div className={styles.outageBannerContent}>
                <strong className={styles.outageBannerSubject}>{ticket.ticketSubject}</strong>
                <span className={styles.outageBannerDate}>{formatOutageDate(latest.createdAt)}</span>
                <p className={styles.outageBannerBody}>{latest.body}</p>
                {comments.length > 1 && (
                  <button
                    type='button'
                    className={styles.outageBannerLink}
                    onClick={() => setShowOutageModal(true)}
                  >
                    Click or tap to view full history
                  </button>
                )}
              </div>
            </div>

            {/* Outage History Modal */}
            {showOutageModal && (
              <div className={styles.outageModalOverlay} onClick={() => setShowOutageModal(false)}>
                <div className={styles.outageModal} onClick={e => e.stopPropagation()}>
                  <div className={styles.outageModalHeader}>
                    <h3>{ticket.ticketSubject}</h3>
                    <button
                      type='button'
                      className={styles.outageModalClose}
                      onClick={() => setShowOutageModal(false)}
                      aria-label='Close'
                    >
                      &times;
                    </button>
                  </div>
                  <div className={styles.outageModalBody}>
                    {comments.map((c, i) => (
                      <div key={i} className={styles.outageModalEntry}>
                        <span className={styles.outageModalDate}>{formatOutageDate(c.createdAt)}</span>
                        <p className={styles.outageModalText}>{c.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {showForm &&
      <FormContainer5050
        imageD={newImage}
        imageM={newImage}
        imageDesc='Cityside Fiber Contact Us'
        loadImage='eager'
        hideDesignElement={true}
        loadingAnimationText='Submitting your request...'
        showLoadingAnimation={showAnim}
        imageFullHeight={true}
      >
        <form ref={formRef} onSubmit={onSubmit} onChange={onChange}>
          <h1 className='headline__h1'>
            <span className='color--dark-blue'>Contact Us</span>
          </h1>
          <p>How can we help you today?</p>

          {/* Radio Buttons */}
          <div className={styles.radioGroup}>
            <label className={styles.radioLabel}>
              <input
                type='radio'
                name='contacttype'
                value='customer'
                checked={inquiryType === 'customer'}
                onChange={() => setInquiryType('customer')}
              />
              <span>Customer</span>
            </label>
            <label className={styles.radioLabel}>
              <input
                type='radio'
                name='contacttype'
                value='general'
                checked={inquiryType === 'general'}
                onChange={() => setInquiryType('general')}
              />
              <span>General Inquiry</span>
            </label>
          </div>

          {/* Customer Fields */}
          {inquiryType === 'customer' && (
            <>
              <p className={styles.infoBlurb}>If you are a new or existing customer, please use the email associated with your MyCity account.</p>

              <div className='input-container'>
                <label htmlFor='email'>Email Address Linked to Your Account</label>
                <input
                  id='email'
                  name='email'
                  type='email'
                  placeholder=''
                  autoComplete='off'
                  required
                />
              </div>

              <div className='input-container'>
                <label>Reason for Contact</label>
                <div className={styles.customSelectContainer} ref={reasonDropdownRef}>
                  <button
                    type='button'
                    className={`${styles.customSelectTrigger} ${reasonDropdownOpen ? styles.open : ''}`}
                    onClick={() => setReasonDropdownOpen(o => !o)}
                    aria-haspopup='listbox'
                    aria-expanded={reasonDropdownOpen}
                  >
                    <span className={selectedReason ? '' : styles.triggerPlaceholder}>
                      {selectedReason ? selectedReason.label : 'Select the option that best matches...'}
                    </span>
                    <svg className={`${styles.triggerArrow} ${reasonDropdownOpen ? styles.triggerArrowOpen : ''}`} width='16' height='16' viewBox='0 0 24 24' fill='currentColor'>
                      <path d='M12 16L6 9h12l-6 7z'/>
                    </svg>
                  </button>

                  {reasonDropdownOpen && (
                    <ul className={styles.customSelectDropdown} role='listbox'>
                      {reasonOptions.map(opt => (
                        <li
                          key={opt.value}
                          className={`${styles.customSelectOption} ${selectedReason?.value === opt.value ? styles.optionSelected : ''}`}
                          onClick={() => { setSelectedReason(opt); setReasonDropdownOpen(false); setShowReasonPrompt(false); }}
                          role='option'
                          aria-selected={selectedReason?.value === opt.value}
                        >
                          <span className={styles.customSelectOptionLabel}>{opt.label}</span>
                          <span className={styles.customSelectOptionDesc}>{opt.desc}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {showReasonPrompt && <p className={styles.errorText}>Please select a reason for contact.</p>}
              </div>

              <div className='input-container'>
                <label htmlFor='description'>Description</label>
                <textarea
                  id='description'
                  name='description'
                  rows={4}
                  placeholder='Please describe your issue or question...'
                  required
                />
              </div>
            </>
          )}

          {/* General Inquiry Fields */}
          {inquiryType === 'general' && (
            <>
              <p className={styles.infoBlurb}>Contact us with questions about Service Availability or to share input about Cityside Fiber and crews in your area.</p>

              <div className='multi-column-container'>
                <div className='input-container'>
                  <label htmlFor='firstname'>First Name</label>
                  <input
                    id='firstname'
                    name='firstname'
                    type='text'
                    autoComplete='off'
                    required
                  />
                </div>

                <div className='input-container'>
                  <label htmlFor='lastname'>Last Name</label>
                  <input
                    id='lastname'
                    name='lastname'
                    type='text'
                    autoComplete='off'
                    required
                  />
                </div>
              </div>

              <div className='multi-column-container'>
                <div className='input-container'>
                  <label htmlFor='email'>Email</label>
                  <input
                    id='email'
                    name='email'
                    type='email'
                    required
                  />
                </div>

                <div className='input-container'>
                  <label htmlFor='phone'>Phone (optional)</label>
                  <input
                    id='phone'
                    name='phone'
                    type='tel'
                    pattern='[0-9]{3}-?[0-9]{3}-?[0-9]{4}'
                    placeholder='XXX-XXX-XXXX'
                  />
                </div>
              </div>

              <div className='input-container'>
                <label htmlFor='description'>Description</label>
                <textarea
                  id='description'
                  name='description'
                  rows={4}
                  placeholder='Please describe your inquiry...'
                  required
                />
              </div>
            </>
          )}

          {showEmailPrompt &&
            <p className={styles.errorText}>Please enter a valid email address.</p>
          }

          <Button
            type='submit'
            className='btn--lg-extra-padding'
            size='md-to-lg'
            fullWidth={true}
          >
            Submit
          </Button>
        </form>
      </FormContainer5050>
      }

      {/* Success */}
      {showSubmissionSuccess &&
      <FormContainer5050
        imageD={newImage}
        imageM={newImage}
        imageDesc='Cityside Fiber Contact Us'
        loadImage='eager'
        hideDesignElement={true}
        imageFullHeight={true}
      >
        <form>
          <h1 className='headline__h1'>
            <span className='color--dark-blue'>Thanks for<br />reaching out!</span>
          </h1>
          {serverMessage
            ? <p style={{whiteSpace: 'pre-line'}}>{serverMessage}</p>
            : <p>We&#39;ve received your request. Our team will get back to you shortly.
                If you need immediate assistance, please email us at
                <a href="mailto:concierge@citysidefiber.com"> concierge@citysidefiber.com</a>.
              </p>
          }
        </form>
      </FormContainer5050>
      }

      {/* Network Error */}
      {showNetworkError &&
      <FormContainer5050
        imageD={newImage}
        imageM={newImage}
        imageDesc='Cityside Fiber Contact Us'
        loadImage='eager'
        hideDesignElement={true}
        imageFullHeight={true}
      >
        <form>
          <h2 className='headline__h2'>
            <span className='color--dark-blue'>
              {isDuplicateTicket ? 'Ticket found!' : <>Hmm, we&#39;re running<br />into a little trouble...</>}
            </span>
          </h2>
          {serverMessage
            ? <p style={{whiteSpace: 'pre-line'}}>{serverMessage}</p>
            : <p>It looks like we&#39;re having some issues submitting your request.
                Please try again later or reach out directly at
                <a href="mailto:concierge@citysidefiber.com"> concierge@citysidefiber.com</a>.
              </p>
          }
        </form>
      </FormContainer5050>
      }
    </>
  );
};

export default ContactUs;